import React, { useState, useEffect, useRef } from 'react';
import '../../styles/components/FuzzySearchForm.scss';
import { FaSearch, FaInfoCircle, FaSpellCheck } from 'react-icons/fa';
import { preprocessQuery, queryToCriteria } from '../../utils/queryProcessor';

const FuzzySearchForm = ({ 
  initialValues = {}, 
  practiceAreas = [],
  organisationTypes = ['SRA Regulated', 'ABS', 'Sole Practitioner', 'Partnership', 'Limited Company'],
  onSearch 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [formValues, setFormValues] = useState({
    name: initialValues.name || '',
    practiceAreas: initialValues.practiceAreas || '',
    organisationType: initialValues.organisationType || '',
    sraNumber: initialValues.sraNumber || '',
    address: initialValues.address || '',
    phone: initialValues.phone || '',
    email: initialValues.email || ''
  });

  // State for enhanced suggestions and processing
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchExplanation, setSearchExplanation] = useState('');
  const [spellSuggestions, setSpellSuggestions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const suggestionsRef = useRef(null);
  
  // Process the search term with enhanced NLP
  const processSearchTerm = (term) => {
    if (!term || term.length === 0) {
      setSuggestions([]);
      setSearchExplanation('');
      setSpellSuggestions([]);
      return;
    }
    
    setIsProcessing(true);
    
    // Use the new query processor
    const processed = preprocessQuery(term);
    
    // Set search explanation
    setSearchExplanation(processed.searchExplanation);
    
    // Set spell suggestions
    setSpellSuggestions(processed.suggestions);
    
    // Generate enhanced suggestions
    const enhancedSuggestions = [];
    
    // Add spell correction suggestions first (highest priority)
    if (processed.suggestions.length > 0) {
      processed.suggestions.forEach(suggestion => {
        enhancedSuggestions.push({
          type: 'spell_correction',
          text: suggestion.suggestion,
          display: `Did you mean: ${suggestion.suggestion}?`,
          confidence: suggestion.confidence,
          icon: 'spell'
        });
      });
    }
    
    // Add practice area suggestions
    if (processed.practiceAreas.length > 0) {
      processed.practiceAreas.forEach(pa => {
        enhancedSuggestions.push({
          type: 'practiceAreas',
          text: pa.area,
          display: `Practice Area: ${pa.area} law`,
          confidence: pa.confidence,
          icon: 'practice'
        });
      });
    }
    
    // Add location suggestions
    if (processed.location) {
      enhancedSuggestions.push({
        type: 'address',
        text: processed.location,
        display: `Location: ${processed.location}`,
        confidence: 0.9,
        icon: 'location'
      });
    }
    
    // Add qualifier suggestions
    processed.qualifiers.forEach(qualifier => {
      enhancedSuggestions.push({
        type: 'qualifier',
        text: qualifier.qualifier,
        display: `Requirement: ${qualifier.qualifier}`,
        confidence: qualifier.confidence,
        icon: 'qualifier'
      });
    });
    
    // Add traditional suggestions for fallback
    const lowerTerm = term.toLowerCase();
    
    // Practice area suggestions from existing list
    practiceAreas.forEach(area => {
      if (area.toLowerCase().includes(lowerTerm) && !enhancedSuggestions.some(s => s.text === area)) {
        enhancedSuggestions.push({
          type: 'practiceAreas',
          text: area,
          display: `Practice Area: ${area}`,
          confidence: 0.7,
          icon: 'practice'
        });
      }
    });
    
    // Organisation type suggestions
    organisationTypes.forEach(type => {
      if (type.toLowerCase().includes(lowerTerm)) {
        enhancedSuggestions.push({
          type: 'organisationType',
          text: type,
          display: `Organisation Type: ${type}`,
          confidence: 0.6,
          icon: 'org'
        });
      }
    });
    
    // SRA number pattern
    if (/^\d*$/.test(lowerTerm)) {
      enhancedSuggestions.push({
        type: 'sraNumber',
        text: term,
        display: `SRA Number: ${term}`,
        confidence: 0.8,
        icon: 'sra'
      });
    }
    
    // Email pattern
    if (lowerTerm.includes('@') || /^[a-z0-9._%+-]+@?[a-z0-9.-]*$/.test(lowerTerm)) {
      enhancedSuggestions.push({
        type: 'email',
        text: term,
        display: `Email: ${term}`,
        confidence: 0.8,
        icon: 'email'
      });
    }
    
    // Phone pattern
    if (/^\+?[\d\s-()]{3,}$/.test(lowerTerm)) {
      enhancedSuggestions.push({
        type: 'phone',
        text: term,
        display: `Phone: ${term}`,
        confidence: 0.8,
        icon: 'phone'
      });
    }
    
    // Postal code pattern
    if (/^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i.test(lowerTerm) || 
        /^[a-z]{1,2}\d{1,2}$/i.test(lowerTerm)) {
      enhancedSuggestions.push({
        type: 'address',
        text: term,
        display: `Postal Code: ${term}`,
        confidence: 0.8,
        icon: 'address'
      });
    }
    
    // Always add name suggestion as fallback
    enhancedSuggestions.push({
      type: 'name',
      text: term,
      display: `Firm Name: ${term}`,
      confidence: 0.5,
      icon: 'name'
    });
    
    // Sort by confidence and limit
    const sortedSuggestions = enhancedSuggestions
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 6);
    
    setSuggestions(sortedSuggestions);
    setIsProcessing(false);
  };
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Debounce the processing
    const timeoutId = setTimeout(() => {
      processSearchTerm(value);
    }, 150);
    
    // Store timeout for cleanup
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    window.searchTimeout = timeoutId;
    
    setShowSuggestions(true);
  };
  
  const handleSuggestionSelect = (suggestion) => {
    // Handle spell corrections differently
    if (suggestion.type === 'spell_correction') {
      setSearchTerm(suggestion.text);
      processSearchTerm(suggestion.text);
      return;
    }
    
    // Update form values based on suggestion type
    setFormValues(prev => ({
      ...prev,
      [suggestion.type]: suggestion.text
    }));
    
    // Clear search bar and hide suggestions
    setSearchTerm('');
    setShowSuggestions(false);
    setSuggestions([]);
    setSearchExplanation('');
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // If there's a search term, process it with NLP first
    if (searchTerm && searchTerm.length > 0) {
      const processed = preprocessQuery(searchTerm);
      const enhancedCriteria = queryToCriteria(processed);
      
      // Merge with existing form values, giving priority to processed data
      const finalCriteria = {
        ...formValues,
        ...enhancedCriteria
      };
      
      onSearch(finalCriteria);
    } else {
      onSearch(formValues);
    }
    
    // Clear search bar after submission
    setSearchTerm('');
    setShowSuggestions(false);
    setSearchExplanation('');
  };
  
  // Handle clicks outside suggestion list
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Update active search filters display - fix for the trim error
  const activeFilters = Object.entries(formValues).filter(([_, value]) => {
    // Check if value exists and is not empty
    if (!value) return false;
    // For strings, check if after trimming they're not empty
    if (typeof value === 'string') return value.trim() !== '';
    // For arrays, check if they have items
    if (Array.isArray(value)) return value.length > 0;
    // For other types, just return true if they exist
    return true;
  });
  
  // Clear a specific filter
  const clearFilter = (key) => {
    setFormValues(prev => ({
      ...prev,
      [key]: ''
    }));
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setFormValues({
      name: '',
      practiceAreas: '',
      organisationType: '',
      sraNumber: '',
      address: '',
      phone: '',
      email: ''
    });
    setSearchTerm('');
    setSearchExplanation('');
  };

  // Get icon for suggestion type
  const getSuggestionIcon = (iconType) => {
    switch (iconType) {
      case 'spell': return <FaSpellCheck className="suggestion-icon spell" />;
      case 'practice': return <FaSearch className="suggestion-icon practice" />;
      case 'location': return <div className="suggestion-icon location">📍</div>;
      case 'sra': return <div className="suggestion-icon sra">#</div>;
      case 'email': return <div className="suggestion-icon email">✉</div>;
      case 'phone': return <div className="suggestion-icon phone">📞</div>;
      case 'address': return <div className="suggestion-icon address">🏠</div>;
      case 'org': return <div className="suggestion-icon org">🏢</div>;
      default: return <FaSearch className="suggestion-icon default" />;
    }
  };
  
  return (
    <div className="fuzzy-search-form-container">
      <form onSubmit={handleSubmit} className="fuzzy-search-form">
        <div className="search-bar-container" ref={suggestionsRef}>
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Try: 'divorce solicitor in London' or 'immigration solicitors'"
              value={searchTerm}
              onChange={handleInputChange}
              className="search-input enhanced"
              autoComplete="off"
            />
            {searchTerm && (
              <button 
                type="button" 
                className="clear-button"
                onClick={() => {
                  setSearchTerm('');
                  setSuggestions([]);
                  setSearchExplanation('');
                }}
              >
                ×
              </button>
            )}
          </div>
          
          {/* Search explanation */}
          {searchExplanation && !showSuggestions && (
            <div className="search-explanation">
              <FaInfoCircle className="explanation-icon" />
              <span>{searchExplanation}</span>
            </div>
          )}
          
          {showSuggestions && suggestions.length > 0 && (
            <ul className="suggestions-list enhanced">
              {suggestions.map((suggestion, index) => (
                <li 
                  key={index} 
                  onClick={() => handleSuggestionSelect(suggestion)}
                  className={`suggestion-item ${suggestion.type} ${suggestion.type === 'spell_correction' ? 'spell-correction' : ''}`}
                >
                  {getSuggestionIcon(suggestion.icon)}
                  <span className="suggestion-text">{suggestion.display}</span>
                  {suggestion.confidence && (
                    <div className="confidence-bar">
                      <div 
                        className="confidence-fill" 
                        style={{ width: `${suggestion.confidence * 100}%` }}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        
        {activeFilters.length > 0 && (
          <div className="active-filters">
            <div className="active-filters-header">
              <span>Active Filters:</span>
              <button 
                type="button" 
                className="clear-all-btn"
                onClick={clearAllFilters}
              >
                Clear All
              </button>
            </div>
            <div className="filter-tags">
              {activeFilters.map(([key, value]) => (
                <div key={key} className="filter-tag">
                  <span className="filter-label">
                    {key === 'name' ? 'Name' : 
                     key === 'practiceAreas' ? 'Practice Area' :
                     key === 'organisationType' ? 'Organisation Type' :
                     key === 'sraNumber' ? 'SRA Number' :
                     key === 'address' ? 'Address' :
                     key === 'phone' ? 'Phone' : 'Email'}:
                  </span>
                  <span className="filter-value">{value}</span>
                  <button 
                    type="button" 
                    className="remove-filter"
                    onClick={() => clearFilter(key)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <button type="submit" className="fuzzy-search-btn enhanced">
          {isProcessing ? (
            <>
              <div className="search-spinner" />
              Processing...
            </>
          ) : (
            <>
              <FaSearch />
              Search Solicitors
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default FuzzySearchForm; 